import api from './api';

export type RenewalMode = 'retomar' | 'refazer';

export interface RenewalChild {
  id: string;
  name: string;
  birthDate: string;
  gender: string | null;
}

export interface RenewalData {
  /**
   * Fluxo SUGERIDO pelo plano atual (copy do app). O modo efetivo depende do
   * plano que o cliente escolher aqui: trocar de plano vira sempre "refazer".
   */
  mode: RenewalMode;
  /**
   * 'upgrade' = link de upgrade de assinante ATIVO: página vira "Fazer upgrade"
   * (seletor só com planos superiores, plano atual fora). Ausente/'renewal' =
   * renovação clássica — backends antigos não mandam o campo.
   */
  purpose?: 'renewal' | 'upgrade';
  subscriptionId: string;
  /** Plano ATUAL da assinatura — vem pré-selecionado no seletor. */
  plan: {
    id: string;
    slug: string | null;
    name: string;
    billingCycle: 'monthly' | 'annual';
    planType: string | null;
    discountedPrice: number;
    originalPrice?: number;
  };
  /**
   * Oferta fechada (automação de renovação, ou painel no upgrade mensal →
   * anual): quando presente é o ÚNICO plano pagável por este link (o backend
   * recusa qualquer outro). Ausente/null = o cliente escolhe.
   */
  offerPlan?: (RenewalData['plan'] & { requiresTurnstile?: boolean }) | null;
  /**
   * Cupom travado no link (Black Friday nov/2026): vem pronto, no formato do
   * `/coupons/validate` — que recusa esse cupom, por isso ele não é validado
   * de novo aqui. O backend cobra com ele seja qual for o cupom enviado; a
   * página o aplica e não deixa trocar nem remover. null/ausente = cupom livre.
   */
  cupomTravado?: {
    code: string;
    discount_type: 'PERCENT' | 'AMOUNT';
    value: number;
    allowed_payment_method: 'pix' | 'credit-card' | null;
    allowed_plan_types: string[] | null;
  } | null;
  /**
   * Upgrade de assinatura mensal: dia (YYYY-MM-DD) em que começa o ano de um
   * plano anual à vista — o vencimento da mensal, já paga até lá. null/ausente
   * = começa no pagamento. Só copy.
   */
  annualStartsAt?: string | null;
  recipient: {
    name: string | null;
    whatsapp: string | null;
  };
  email: string | null;
  shipping: {
    zip: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    neighborhood: string | null;
    city: string | null;
    state: string | null;
  };
  children: RenewalChild[];
}

export const RenewalService = {
  async getByToken(token: string): Promise<RenewalData> {
    // Timestamp anti-cache (mesmo motivo do GET /payments/pix): o navegador
    // guarda a resposta — inclusive o 410 de link expirado — e a tela voltaria
    // a mostrar um estado velho num link novo.
    const { data } = await api.get(`/renewals/${encodeURIComponent(token)}?t=${Date.now()}`);
    return data?.data ?? data;
  },

  /**
   * Link curto (`/r/:code`): troca o código pelo token do link. Código
   * desconhecido responde 404 `link_invalido`; vencido/revogado só aparece no
   * getByToken, com a copy de sempre.
   */
  async resolveShortLink(code: string): Promise<{ token: string; cupom: string | null }> {
    const { data } = await api.get(`/renewals/r/${encodeURIComponent(code)}`);
    return data?.data ?? data;
  },
};

/**
 * O clientId não vem no GET /renewals (a resposta é o mínimo pra hidratar a tela),
 * mas o POST /payments exige. Ele está dentro do próprio token, que o navegador já
 * tem em mãos: lemos o payload sem verificar assinatura. O backend reconfere a
 * assinatura do token E que `sub` bate com o clientId enviado, então valor
 * adulterado aqui só derruba a cobrança (falha-fechada), nunca cobra outra conta.
 */
export function readClientIdFromRenewalToken(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return typeof parsed?.sub === 'string' && parsed.sub ? parsed.sub : null;
  } catch {
    return null;
  }
}
